import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  CheckIcon, 
  ClockIcon,
  UserIcon,
  AcademicCapIcon,
  DocumentIcon
} from '@heroicons/react/24/outline';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import PersonalDetailsStep from '../components/public/PersonalDetailsStep';
import EducationStep from '../components/public/EducationStep';
import DocumentUploadStep from '../components/public/DocumentUploadStep';
import { submitApplication, uploadFile, generateNextApplicationId, returnIdToPool, checkIfRegistrationIdExists, submitEnquiry } from '../services/firebaseService';
import { Application } from '../types';
import { sendApplicationReceivedEmail } from '../services/emailService';
import { getGlobalSettings } from '../services/settingsService';

interface FormData {
  personal?: any;
  education?: any;
  documents?: any;
}

const ApplicationPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const stepParam = searchParams.get('step');
  const stepFromUrl = stepParam ? parseInt(stepParam, 10) : 1;
  const currentStep = (stepFromUrl >= 1 && stepFromUrl <= 3) ? stepFromUrl : 1;

  const [formData, setFormData] = useState<FormData>(() => {
    try {
      const saved = sessionStorage.getItem('okreed_form_draft');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch {}
    return {};
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [applicationId, setApplicationId] = useState<string>('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const navigate = useNavigate();

  // Enquiry Form states
  const [enquiryForm, setEnquiryForm] = useState({
    name: '',
    email: '',
    mobileNumber: '',
    subject: 'General Enquiry',
    message: ''
  });
  const [enquirySubmitting, setEnquirySubmitting] = useState(false);
  const [enquirySubmitted, setEnquirySubmitted] = useState(false);
  const [enquiryError, setEnquiryError] = useState('');

  const storedUser = localStorage.getItem('adminUser');
  const loggedInUser = storedUser ? JSON.parse(storedUser) : null;
  const isAuthorizedStaff = loggedInUser && ['deo', 'accounts', 'admin', 'super_admin'].includes(loggedInUser.role);

  const handleEnquirySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!enquiryForm.name || !enquiryForm.email || !enquiryForm.mobileNumber || !enquiryForm.message) {
      setEnquiryError('Please fill in all required fields.');
      return;
    }
    setEnquirySubmitting(true);
    setEnquiryError('');
    try {
      await submitEnquiry(enquiryForm);
      setEnquirySubmitted(true);
    } catch (err: any) {
      console.error('Enquiry submission error:', err);
      setEnquiryError('Failed to submit enquiry. Please try again.');
    } finally {
      setEnquirySubmitting(false);
    }
  };

  const steps = [
    {
      id: 1,
      title: 'Personal Details',
      description: 'Basic information (Mandatory)',
      icon: UserIcon,
      isComplete: !!formData.personal,
      isActive: currentStep === 1
    },
    {
      id: 2,
      title: 'Education (Optional)',
      description: 'Qualifications (Optional)',
      icon: AcademicCapIcon,
      isComplete: !!formData.education,
      isActive: currentStep === 2
    },
    {
      id: 3,
      title: 'Documents (Optional)',
      description: 'Upload documents (Optional)',
      icon: DocumentIcon,
      isComplete: !!formData.documents,
      isActive: currentStep === 3
    }
  ];

  const handlePersonalDetailsNext = (data: any, shouldDirectSubmit?: boolean) => {
    const updated = { ...formData, personal: data };
    setFormData(updated);
    try {
      sessionStorage.setItem('okreed_form_draft', JSON.stringify({ ...updated, documents: undefined }));
    } catch {}

    if (shouldDirectSubmit) {
      handleDocumentsNext(undefined, data);
      return;
    }

    setSearchParams({ step: '2' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleEducationNext = (data: any) => {
    const updated = { ...formData, education: data };
    setFormData(updated);
    try {
      sessionStorage.setItem('okreed_form_draft', JSON.stringify({ ...updated, documents: undefined }));
    } catch {}
    setSearchParams({ step: '3' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleEducationSkip = () => {
    setSearchParams({ step: '3' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleEducationSkipAndSubmit = () => {
    handleDocumentsNext(undefined);
  };

  const handleBackToStep = (targetStep: number, currentStepData?: any) => {
    let updated = { ...formData };
    if (currentStepData) {
      if (currentStep === 2) {
        updated = { ...updated, education: currentStepData };
      } else if (currentStep === 3) {
        updated = { ...updated, documents: currentStepData };
      }
      setFormData(updated);
      try {
        sessionStorage.setItem('okreed_form_draft', JSON.stringify({ ...updated, documents: undefined }));
      } catch {}
    }
    if (targetStep === 1) {
      setSearchParams({});
    } else {
      setSearchParams({ step: targetStep.toString() });
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDocumentsNext = async (data?: any, overridePersonal?: any) => {
    setIsSubmitting(true);
    let appId: string | null = null;
    let isSpecialId = false;
    const personal = overridePersonal || formData.personal;
    try {
      if (personal?.specialId && personal.specialId.trim() !== '') {
        const rawSeq = parseInt(personal.specialId.trim(), 10);
        if (!isNaN(rawSeq) && rawSeq >= 1 && rawSeq <= 1000) {
          isSpecialId = true;
          const seq = String(rawSeq).padStart(4, '0');
          const now = new Date();
          const year = now.getFullYear();
          const month = String(now.getMonth() + 1).padStart(2, '0');
          const proposedId = `KOP-${year}-${month}-${seq}`;
          
          const exists = await checkIfRegistrationIdExists(proposedId);
          if (exists) {
            throw new Error(`Special ID ${seq} is already allotted to another candidate. Please choose a different one.`);
          }
          appId = proposedId;
        } else {
          appId = await generateNextApplicationId(false);
        }
      } else {
        // Get the real sequential registration ID first
        appId = await generateNextApplicationId(false);
      }
      console.log('Starting file upload process for application:', appId);
      
      // Upload documents to Firebase Storage
      const uploadPromises = [];
      const docPayload = data?.documents || {};
      
      // Upload photo
      if (docPayload.photo) {
        console.log('Uploading photo:', docPayload.photo.name);
        uploadPromises.push(
          uploadFile(docPayload.photo, `applications/${appId}/photo`)
            .then(url => {
              console.log('Photo uploaded successfully:', url);
              return { photo: url };
            })
            .catch(error => {
              console.error('Error uploading photo:', error);
              throw new Error(`Failed to upload photo: ${error.message}`);
            })
        );
      }

      if (data?.useMasterAsset) {
        // Upload master asset
        if (docPayload.masterAsset) {
          console.log('Uploading master asset:', docPayload.masterAsset.name);
          uploadPromises.push(
            uploadFile(docPayload.masterAsset, `applications/${appId}/masterAsset`)
              .then(url => {
                console.log('Master asset uploaded successfully:', url);
                return { masterAsset: url };
              })
              .catch(error => {
                console.error('Error uploading master asset:', error);
                throw new Error(`Failed to upload master asset: ${error.message}`);
              })
          );
        }
      } else {
        // Upload 10th certificate
        if (docPayload.class10Certificate) {
          console.log('Uploading 10th certificate:', docPayload.class10Certificate.name);
          uploadPromises.push(
            uploadFile(docPayload.class10Certificate, `applications/${appId}/class10Certificate`)
              .then(url => {
                console.log('10th certificate uploaded successfully:', url);
                return { class10Certificate: url };
              })
              .catch(error => {
                console.error('Error uploading 10th certificate:', error);
                throw new Error(`Failed to upload 10th certificate: ${error.message}`);
              })
          );
        }
        
        // Upload 12th certificate
        if (docPayload.class12Certificate) {
          console.log('Uploading 12th certificate:', docPayload.class12Certificate.name);
          uploadPromises.push(
            uploadFile(docPayload.class12Certificate, `applications/${appId}/class12Certificate`)
              .then(url => {
                console.log('12th certificate uploaded successfully:', url);
                return { class12Certificate: url };
              })
              .catch(error => {
                console.error('Error uploading 12th certificate:', error);
                throw new Error(`Failed to upload 12th certificate: ${error.message}`);
              })
          );
        }

        // Upload Aadhaar card
        if (docPayload.aadhaarCard) {
          console.log('Uploading Aadhaar card:', docPayload.aadhaarCard.name);
          uploadPromises.push(
            uploadFile(docPayload.aadhaarCard, `applications/${appId}/aadhaarCard`)
              .then(url => {
                console.log('Aadhaar card uploaded successfully:', url);
                return { aadhaarCard: url };
              })
              .catch(error => {
                console.error('Error uploading Aadhaar card:', error);
                throw new Error(`Failed to upload Aadhaar card: ${error.message}`);
              })
          );
        }

        // Upload caste certificate
        if (docPayload.casteCertificate) {
          console.log('Uploading caste certificate:', docPayload.casteCertificate.name);
          uploadPromises.push(
            uploadFile(docPayload.casteCertificate, `applications/${appId}/casteCertificate`)
              .then(url => {
                console.log('Caste certificate uploaded successfully:', url);
                return { casteCertificate: url };
              })
              .catch(error => {
                console.error('Error uploading caste certificate:', error);
                throw new Error(`Failed to upload caste certificate: ${error.message}`);
              })
          );
        }

        // Upload degree certificate (optional)
        if (docPayload.degreeCertificate) {
          console.log('Uploading degree certificate:', docPayload.degreeCertificate.name);
          uploadPromises.push(
            uploadFile(docPayload.degreeCertificate, `applications/${appId}/degreeCertificate`)
              .then(url => {
                console.log('Degree certificate uploaded successfully:', url);
                return { degreeCertificate: url };
              })
              .catch(error => {
                console.error('Error uploading degree certificate:', error);
                throw new Error(`Failed to upload degree certificate: ${error.message}`);
              })
          );
        }
      }
      
      // Upload sports certificates
      const sportsFiles: File[] = Array.isArray(docPayload.sportsCertificates) ? docPayload.sportsCertificates : [];
      const sportsCertificatePromises = sportsFiles.map((file: File, index: number) => {
        console.log(`Uploading sports certificate ${index + 1}:`, file.name);
        return uploadFile(file, `applications/${appId}/sports_certificate_${index + 1}`)
          .then(url => {
            console.log(`Sports certificate ${index + 1} uploaded successfully:`, url);
            return url;
          })
          .catch(error => {
            console.error(`Error uploading sports certificate ${index + 1}:`, error);
            throw new Error(`Failed to upload sports certificate ${index + 1}: ${error.message}`);
          });
      });
      
      console.log('Waiting for all uploads to complete...');
      const [documentUploads, sportsCertificateUrls] = await Promise.all([
        Promise.all(uploadPromises),
        Promise.all(sportsCertificatePromises)
      ]);
      
      console.log('All uploads completed successfully');
      
      // Combine uploaded document URLs
      const documentUrls = documentUploads.reduce((acc, upload) => ({ ...acc, ...upload }), {});
      
      // Prepare application data
      const applicationData: Omit<Application, 'id' | 'status' | 'submittedAt' | 'updatedAt'> = {
        ...personal,
        ...(formData.education ? formData.education : {}),
        documents: {
          ...documentUrls,
          sportsCertificates: sportsCertificateUrls
        }
      };
      
      // Submit application to Firestore (pass the pre-generated ID)
      const submittedApplicationId = await submitApplication({
        ...applicationData,
        id: appId as any // We override the ID inside submitApplication now
      });
      setApplicationId(submittedApplicationId);
      
      // Get settings to check if email is enabled
      const settings = await getGlobalSettings();
      
      // Send email notification if enabled in settings
      if (settings?.emailNotificationsEnabled) {
        try {
          console.log('Sending email notifications...');
          
          // Construct full application object
          const fullApplication: Application = {
            id: submittedApplicationId,
            status: 'submitted',
            submittedAt: new Date(),
            updatedAt: new Date(),
            ...applicationData
          };
          
          // Send confirmation email to applicant
          const emailSent = await sendApplicationReceivedEmail(fullApplication);
          
          if (emailSent) {
            console.log('Application received email sent successfully to applicant');
          } else {
            console.warn('Failed to send application received email to applicant');
          }
        } catch (emailError) {
          console.error('Error sending email notifications:', emailError);
        }
      }
      
      try {
        sessionStorage.removeItem('okreed_form_draft');
      } catch {}
      setIsSubmitted(true);
      
    } catch (error) {
      console.error('Error submitting application:', error);
      
      // Provide more specific error messages
      let errorMessage = 'There was an error submitting your application. Please try again.';
      
      if (error instanceof Error) {
        if (error.message.includes('CORS')) {
          errorMessage = 'File upload failed due to security restrictions. Please contact the administrator to resolve this issue.';
        } else if (error.message.includes('Failed to upload')) {
          errorMessage = `File upload error: ${error.message}. Please check your internet connection and try again.`;
        } else if (error.message.includes('Missing or insufficient permissions')) {
          errorMessage = 'Database access denied. Please contact the administrator to resolve this issue.';
        } else {
          errorMessage = `Error: ${error.message}`;
        }
        
        // If we already generated an appId but submission failed, we should return it to the pool (if not specialId)
        if (appId && !isSpecialId) {
          await returnIdToPool(appId);
        }
        
        alert(errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-2xl mx-auto p-8">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            {/* Success Icon */}
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckIcon className="w-10 h-10 text-green-600" />
            </div>
            
            {/* Success Message */}
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Registration Submitted Successfully!
            </h1>
            
            <p className="text-lg text-gray-600 mb-6">
              Thank you for your interest in Kreed Odisha. Your registration has been received and is under review.
            </p>
            
            {/* Registration ID */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-600 mb-1">Your Registration ID</p>
              <p className="text-2xl font-bold text-primary-600">{applicationId}</p>
              <p className="text-xs text-gray-500 mt-1">
                Please save this ID for future reference
              </p>
            </div>
            
            {/* Next Steps */}
            <div className="text-left bg-blue-50 rounded-lg p-6 mb-6">
              <h3 className="font-semibold text-blue-900 mb-3">What happens next?</h3>
              <ul className="space-y-2 text-blue-800">
                <li className="flex items-start space-x-2">
                  <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
                  <span>Our team will review your registration within 7-10 working days</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
                  <span>You will receive updates via email and SMS</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
                  <span>Shortlisted candidates will be contacted for further assessment</span>
                </li>
              </ul>
            </div>
            
            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/')}
                className="btn-primary"
              >
                Back to Home
              </button>
              <button
                onClick={() => window.print()}
                className="btn-secondary"
              >
                Print Confirmation
              </button>
            </div>
            
            {/* Contact Info */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-600">
                For any queries, contact us at:{' '}
                <a href="mailto:kreedodisha@gmail.com" className="text-primary-600 hover:underline">
                  kreedodisha@gmail.com
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthorizedStaff) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header transparent={false} />
        
        <main className="pt-40 pb-16">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h1 className="text-4xl font-bold text-gray-900 mb-4">
                Kreed Odisha Public Portal
              </h1>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
                Candidate registrations are restricted to authorized <span className="font-bold text-gray-900">Data Entry Operators (DEOs)</span> and administrators. 
                If you are a candidate seeking to enroll, please contact an authorized registration coordinator.
              </p>
              <div className="mt-4">
                <button
                  onClick={() => navigate('/admin/login')}
                  className="bg-primary-600 hover:bg-primary-700 text-white font-medium px-6 py-2.5 rounded-lg text-sm transition-colors shadow-sm"
                >
                  DEO / Admin Login
                </button>
              </div>
            </div>

            {/* Enquiry Section */}
            <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden transition-all duration-300 hover:shadow-2xl">
              <div className="bg-gradient-to-r from-primary-600 to-success-600 px-8 py-6 text-white">
                <h2 className="text-2xl font-bold">Have Queries? Contact Us</h2>
                <p className="text-primary-100 text-sm mt-1">Submit your details below and a coordinator will assist you.</p>
              </div>
              
              <div className="p-8">
                {enquirySubmitted ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckIcon className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Enquiry Submitted!</h3>
                    <p className="text-gray-600">Thank you for reaching out. We will review your query and contact you soon.</p>
                    <button
                      onClick={() => setEnquirySubmitted(false)}
                      className="mt-6 text-sm font-semibold text-primary-600 hover:text-primary-800"
                    >
                      Submit another query
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleEnquirySubmit} className="space-y-6">
                    {enquiryError && (
                      <div className="bg-red-50 border-l-4 border-red-500 p-4 text-sm text-red-700 rounded-r-lg">
                        {enquiryError}
                      </div>
                    )}
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">Full Name</label>
                      <input
                        type="text"
                        value={enquiryForm.name}
                        onChange={(e) => setEnquiryForm({ ...enquiryForm, name: e.target.value })}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-900 placeholder-gray-400"
                        placeholder="Enter your full name"
                        required
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-800 mb-2">Email Address</label>
                        <input
                          type="email"
                          value={enquiryForm.email}
                          onChange={(e) => setEnquiryForm({ ...enquiryForm, email: e.target.value })}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-900 placeholder-gray-400"
                          placeholder="name@example.com"
                          required
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-semibold text-gray-800 mb-2">Mobile Number</label>
                        <input
                          type="tel"
                          value={enquiryForm.mobileNumber}
                          onChange={(e) => setEnquiryForm({ ...enquiryForm, mobileNumber: e.target.value })}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-900 placeholder-gray-400"
                          placeholder="10-digit number"
                          maxLength={10}
                          required
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">Subject</label>
                      <select
                        value={enquiryForm.subject}
                        onChange={(e) => setEnquiryForm({ ...enquiryForm, subject: e.target.value })}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-900 font-medium"
                      >
                        <option value="General Enquiry">General Enquiry</option>
                        <option value="Registration Assistance">Registration Assistance</option>
                        <option value="Document Verification Query">Document Verification Query</option>
                        <option value="Training Selection Query">Training Selection Query</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">Enquiry Details</label>
                      <textarea
                        value={enquiryForm.message}
                        onChange={(e) => setEnquiryForm({ ...enquiryForm, message: e.target.value })}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-900 placeholder-gray-400"
                        rows={4}
                        placeholder="Write details of your query..."
                        required
                      />
                    </div>
                    
                    <button
                      type="submit"
                      disabled={enquirySubmitting}
                      className="w-full bg-gradient-to-r from-primary-600 to-success-600 hover:from-primary-700 hover:to-success-700 text-white font-bold py-3 px-4 rounded-xl shadow-lg transform transition duration-300 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center font-semibold"
                    >
                      {enquirySubmitting ? 'Submitting...' : 'Submit Enquiry'}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </main>
        
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header transparent={false} />
      
      <main className="pt-40 pb-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Kreed Odisha Registration Form
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Take the first step towards your sporting excellence journey. 
              Complete the registration form to join Odisha's premier sports development program.
            </p>
          </div>

          {/* Progress Steps */}
          <div className="max-w-4xl mx-auto mb-12">
            <div className="flex items-center justify-between">
              {steps.map((step, index) => (
                <div 
                  key={step.id} 
                  className={`flex items-center ${
                    step.id < currentStep || formData.personal
                      ? 'cursor-pointer hover:opacity-80 transition-opacity' 
                      : ''
                  }`}
                  onClick={() => {
                    if (step.id < currentStep) {
                      handleBackToStep(step.id);
                    } else if (formData.personal) {
                      setSearchParams({ step: step.id.toString() });
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }
                  }}
                  title={
                    step.id < currentStep 
                      ? `Click to go back to ${step.title}` 
                      : formData.personal
                      ? `Click to navigate to ${step.title}`
                      : undefined
                  }
                >
                  {/* Step Circle */}
                  <div className="flex items-center">
                    <div
                      className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 ${
                        step.isComplete
                          ? 'bg-green-600 text-white'
                          : step.isActive
                          ? 'bg-primary-600 text-white'
                          : 'bg-gray-200 text-gray-500'
                      }`}
                    >
                      {step.isComplete ? (
                        <CheckIcon className="w-6 h-6" />
                      ) : step.isActive ? (
                        <ClockIcon className="w-6 h-6" />
                      ) : (
                        <step.icon className="w-6 h-6" />
                      )}
                    </div>
                    
                    {/* Step Info */}
                    <div className="ml-4 hidden sm:block">
                      <p className={`font-semibold ${
                        step.isActive ? 'text-primary-600' : 'text-gray-900'
                      }`}>
                        {step.title}
                      </p>
                      <p className="text-sm text-gray-500">{step.description}</p>
                    </div>
                  </div>
                  
                  {/* Connector Line */}
                  {index < steps.length - 1 && (
                    <div
                      className={`flex-1 h-1 mx-4 transition-all duration-300 ${
                        step.isComplete ? 'bg-green-600' : 'bg-gray-200'
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Form Steps */}
          <div className="max-w-6xl mx-auto">
            {currentStep === 1 && (
              <PersonalDetailsStep
                data={formData.personal || {}}
                onNext={handlePersonalDetailsNext}
                onBack={() => navigate('/')}
                isSubmitting={isSubmitting}
              />
            )}
            
            {currentStep === 2 && (
              <EducationStep
                data={formData.education || {}}
                canSkip={true}
                onNext={handleEducationNext}
                onSkip={handleEducationSkip}
                onSkipAndSubmit={handleEducationSkipAndSubmit}
                onBack={(data) => handleBackToStep(1, data)}
                isSubmitting={isSubmitting}
              />
            )}
            
            {currentStep === 3 && (
              <DocumentUploadStep
                data={formData.documents || {}}
                caste={formData.personal?.caste}
                canSkip={true}
                onNext={handleDocumentsNext}
                onSkipAndSubmit={() => handleDocumentsNext(undefined)}
                onBack={(data) => handleBackToStep(2, data)}
                isSubmitting={isSubmitting}
              />
            )}
          </div>

          {/* Help Section */}
          <div className="max-w-4xl mx-auto mt-16">
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Need Help?</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="text-center">
                  <div className="w-12 h-12 bg-success-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                    <ClockIcon className="w-6 h-6 text-success-600" />
                  </div>
                  <h4 className="font-semibold mb-2">Office Hours</h4>
                  <p className="text-gray-600 text-sm">
                    Monday to Friday: 9:00 AM - 6:00 PM<br />
                    Saturday: 9:00 AM - 1:00 PM
                  </p>
                </div>
                
                <div className="text-center">
                  <div className="w-12 h-12 bg-warning-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                    <DocumentIcon className="w-6 h-6 text-warning-600" />
                  </div>
                  <h4 className="font-semibold mb-2">Email Support</h4>
                  <p className="text-gray-600 text-sm">
                    Send us your queries and we'll respond within 24 hours
                  </p>
                  <p className="text-primary-600 font-medium mt-2">kreedodisha@gmail.com</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default ApplicationPage;